import { Bell, HeartHandshake, Check, X as XIcon, UserPlus, Trophy, Sparkles } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { useRelationshipStore } from '../store/useRelationshipStore';
import { useDatingStore } from '../store/useDatingStore';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { notifications, clearNotification } = useAuthStore();
  const { pendingRequests, getPendingRequests, acceptRequest, rejectRequest } = useRelationshipStore();
  const { incomingRequests, getFriendRequests, acceptFriendRequest, rejectFriendRequest } = useDatingStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    getPendingRequests();
    getFriendRequests();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const totalCount = notifications.length + (pendingRequests?.length || 0) + (incomingRequests?.length || 0);

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-ghost btn-circle relative"
        title="Notifications & Requests"
      >
        <Bell size={20} />
        {totalCount > 0 && (
          <span className="absolute top-1 right-1 bg-primary text-primary-content text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center animate-pulse">
            {totalCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 md:w-96 bg-base-100 rounded-3xl shadow-2xl z-50 border border-base-300 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-4 bg-base-200/70 border-b border-base-300 flex items-center justify-between">
            <h3 className="font-extrabold text-sm flex items-center gap-2">
              <Bell className="size-4 text-primary" /> Notifications & Requests ({totalCount})
            </h3>
            {totalCount > 0 && (
              <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                Active Updates
              </span>
            )}
          </div>

          <div className="p-3 max-h-96 overflow-y-auto space-y-3">
            
            {/* 1. Friend Requests Section */}
            {incomingRequests?.map((user) => (
              <div key={user._id} className="p-3 bg-primary/5 border border-primary/20 rounded-2xl space-y-2">
                <div className="flex items-center gap-3">
                  <img src={user.profilePic || "/avatar.png"} className="w-10 h-10 rounded-xl object-cover ring-2 ring-primary/30" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold truncate">{user.fullName}</p>
                    <p className="text-[11px] text-primary font-semibold">Sent you a friend request</p>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button 
                    onClick={async () => {
                      await acceptFriendRequest(user._id);
                    }}
                    className="btn btn-xs btn-primary flex-1 rounded-lg gap-1 font-bold"
                  >
                    <Check className="size-3" /> Accept
                  </button>
                  <button 
                    onClick={async () => {
                      await rejectFriendRequest(user._id);
                    }}
                    className="btn btn-xs btn-ghost bg-base-300 flex-1 rounded-lg"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}

            {/* 2. Relationship Exclusive Proposals */}
            {pendingRequests?.map((req) => {
              const reqId = req._id || req.id;
              const senderName = req.senderId?.fullName || req.senderName || "Someone";
              return (
                <div key={reqId} className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-2xl space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-500/20 rounded-xl text-rose-500">
                      <HeartHandshake className="size-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold">{senderName} proposed!</p>
                      <p className="text-[11px] text-rose-500 font-semibold">Wants to link partner spaces</p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button 
                      onClick={async () => { 
                        await acceptRequest(reqId); 
                        setIsOpen(false); 
                        navigate('/our-space');
                      }}
                      className="btn btn-xs btn-secondary flex-1 rounded-lg font-bold"
                    >
                      <Check className="size-3" /> Accept Link
                    </button>
                    <button 
                      onClick={() => rejectRequest(reqId)}
                      className="btn btn-xs btn-outline btn-error flex-1 rounded-lg"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              );
            })}

            {/* 3. All System & Activity Notifications */}
            {notifications.map((notif) => (
              <div 
                key={notif.id} 
                className="p-3 bg-base-200/50 hover:bg-base-200 border border-base-300/50 rounded-2xl cursor-pointer transition-all flex items-start gap-2.5"
                onClick={() => {
                  if (notif.onClick) notif.onClick();
                  clearNotification(notif.id);
                  setIsOpen(false);
                }}
              >
                <div className="p-1.5 bg-primary/10 text-primary rounded-lg mt-0.5">
                  <Sparkles className="size-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-base-content/90 leading-tight">{notif.message}</p>
                  <span className="text-[10px] text-base-content/50 mt-1 block">
                    {notif.timestamp || "Just now"}
                  </span>
                </div>
              </div>
            ))}

            {totalCount === 0 && (
              <div className="text-center py-8 text-base-content/50">
                <Bell className="size-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs font-bold">No new notifications</p>
                <p className="text-[10px] opacity-70">You are all caught up!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
